import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent } from "../../components/ui/Card";
import {
  Plus, Copy, Activity, Lock, Users, Folder,
  Zap, Shield, Key, FileCode, ChevronRight, History, ExternalLink
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Button from "../../components/ui/Button";
import { dashboardAPI } from "../../services/api";
import SEO from "../../components/shared/SEO";

const timeAgo = (dateString) => {
  const diff = Math.floor((new Date() - new Date(dateString)) / 1000);
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 84600) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const getActivityIcon = (action) => {
  if (action.includes('SECRETS')) return <Key size={14} className="text-orange-400" />;
  if (action.includes('ROLLBACK')) return <History size={14} className="text-red-400" />;
  if (action.includes('TOKEN')) return <Shield size={14} className="text-green-400" />;
  return <Zap size={14} className="text-blue-400" />;
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [showAllActivity, setShowAllActivity] = useState(false);
  const [data, setData] = useState({
    stats: { totalProjects: 0, totalSecrets: 0, totalMembers: 0, activeTokens: 0 },
    projects: [],
    recentActivity: []
  });

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const result = await dashboardAPI.getStats();
        setData(result);
      } catch (error) {
        console.error("Failed to fetch stats");
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  if (loading) return (
    <div className="flex flex-col items-center justify-center min-h-[60vh]">
      <div className="w-8 h-8 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin mb-4" />
      <p className="text-gray-500 font-bold uppercase tracking-widest text-[10px]">Synchronizing Vault...</p>
    </div>
  );

  return (
    <>
      <SEO title="Overview | Command Center" description="Real-time status of your synchronized projects and active environment clusters." noIndex={true} />
      <div className="max-w-7xl mx-auto space-y-10">

        {/* --- Header Section --- */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <h1 className="text-4xl font-black text-white tracking-tighter">Console Overview</h1>
            <p className="text-gray-500 font-medium mt-1">Global status of your encrypted infrastructure.</p>
          </div>
          {/* <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" className="hidden md:flex">
                <History size={14} className="mr-2" /> Audit Logs
            </Button>
            <Button onClick={() => navigate('/dashboard/projects')} variant="primary" size="sm">
                <Plus size={16} className="mr-2" /> New Project
            </Button>
        </div> */}
        </div>

        {/* --- Stats Grid --- */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: "Active Projects", val: data.stats.totalProjects, icon: Folder, color: "text-blue-400", bg: "bg-blue-400/5" },
            { label: "Total Secrets", val: data.stats.totalSecrets, icon: Lock, color: "text-orange-400", bg: "bg-orange-400/5" },
            { label: "Active Tokens", val: data.stats.activeTokens, icon: Shield, color: "text-green-400", bg: "bg-green-400/5" },
            { label: "Team Members", val: data.stats.totalMembers, icon: Users, color: "text-purple-400", bg: "bg-purple-400/5" },
          ].map((stat, i) => (
            <motion.div
              key={i}
              whileHover={{ y: -2 }}
              className="bg-[#0a0a0a] border border-white/5 p-6 rounded-[1.5rem] relative overflow-hidden group transition-all hover:border-white/10"
            >
              <div className={`absolute top-0 right-0 w-16 h-16 ${stat.bg} blur-2xl opacity-50 group-hover:opacity-100 transition-opacity`} />
              <div className="relative z-10 flex flex-col gap-4">
                <div className={`w-10 h-10 rounded-xl ${stat.bg} flex items-center justify-center ${stat.color} border border-white/5`}>
                  <stat.icon size={18} />
                </div>
                <div>
                  <p className="text-2xl font-black text-white">{stat.val}</p>
                  <p className="text-[10px] font-black uppercase tracking-widest text-gray-600 group-hover:text-gray-400 transition-colors">{stat.label}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* --- Main Content --- */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

          {/* Left: Projects - Modern Table-less List */}
          <div className="lg:col-span-8 space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-black text-white tracking-tight flex items-center gap-3">
                <div className="w-2 h-6 bg-orange-500 rounded-full" />
                Active Projects
              </h2>
              <Link to="/dashboard/projects" className="text-xs font-black uppercase tracking-widest text-orange-500 hover:underline">View All</Link>
            </div>

            <div className="grid gap-3">
              {data.projects.length === 0 ? (
                <div className="p-12 text-center bg-white/[0.02] border border-dashed border-white/10 rounded-3xl text-gray-600 text-sm font-medium">
                  No active projects found.
                </div>
              ) : (
                data.projects.map((p) => (
                  <motion.div
                    key={p._id}
                    whileHover={{ x: 4 }}
                    onClick={() => navigate(`/dashboard/projects/${p._id}`)}
                    className="group flex items-center justify-between p-5 bg-[#0a0a0a] border border-white/5 rounded-[1.25rem] hover:bg-white/[0.03] hover:border-white/10 transition-all cursor-pointer"
                  >
                    <div className="flex items-center gap-5">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500/10 to-red-600/10 flex items-center justify-center text-orange-500 font-black border border-orange-500/20 shadow-inner group-hover:scale-110 transition-transform">
                        {p.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h3
                          className="
    font-bold text-white
    group-hover:text-orange-500 transition-colors

    truncate
    max-w-[220px] sm:max-w-[260px] md:max-w-[320px] lg:max-w-[380px]
  "
                          title={p.name}
                        >
                          {p.name}
                        </h3>
                        {/* <h3 className="font-bold text-white group-hover:text-orange-500 transition-colors">
                        {p.name}
                      </h3> */}
                        <div className="flex items-center gap-3 text-[10px] font-black uppercase tracking-widest text-gray-600 mt-1">
                          <span className="flex items-center gap-1"><Lock size={10} /> {p.secretCount} Secrets</span>
                          <span className="w-1 h-1 bg-gray-800 rounded-full" />
                          <span className="flex items-center gap-1"><Users size={10} /> {p.memberCount} Members</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="hidden md:block text-right">
                        <p className="text-[10px] font-black uppercase tracking-widest text-gray-700">Last Sync</p>
                        <p className="text-xs text-gray-500 font-medium">{timeAgo(p.updatedAt)}</p>
                      </div>
                      <ChevronRight size={18} className="text-gray-700 group-hover:text-orange-500 transition-colors" />
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          </div>

          {/* Right: Modern Activity Journal */}
          <div className="lg:col-span-4 space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-black text-white tracking-tight">Security Journal</h2>
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            </div>

            <div className="bg-[#0a0a0a] border border-white/5 rounded-[2rem] p-6 relative overflow-hidden">
              <div className={`space-y-6 transition-all duration-500 ${showAllActivity ? 'max-h-fit' : 'max-h-[400px] overflow-hidden'}`}>

                {data.recentActivity.length === 0 ? (
                  <div className="py-20 text-center">
                    <Activity className="mx-auto text-gray-800 mb-4" size={32} />
                    <p className="text-gray-600 text-xs font-black uppercase tracking-widest">No Recent Logs</p>
                  </div>
                ) : (
                  data.recentActivity.map((log, idx) => (
                    <div key={log._id} className="flex gap-4 group relative">
                      {/* Visual Connector Line */}
                      {idx !== data.recentActivity.length - 1 && (
                        <div className="absolute left-[15px] top-8 bottom-[-24px] w-[2px] bg-white/[0.03]" />
                      )}

                      <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0 group-hover:border-orange-500/30 transition-colors">
                        {getActivityIcon(log.action)}
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-400 leading-relaxed break-words">
                          <span className="text-white font-bold">{log.user?.name || 'System'}</span>
                          <span className="mx-1 opacity-50">{log.action.toLowerCase().replace('_', ' ')}</span>
                          <span
                            onClick={() => navigate(`/dashboard/projects/${log.project?._id}`)}
                            className="text-orange-500/80 cursor-pointer hover:underline font-bold"
                          >
                            {log.project?.name}
                          </span>
                        </p>
                        <p className="text-[10px] text-gray-600 font-black uppercase tracking-tighter mt-1">{timeAgo(log.createdAt)}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Gradient Overlay for "See More" */}
              {!showAllActivity && data.recentActivity.length > 4 && (
                <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0a0a0a] to-transparent z-10" />
            )}

              {/* Expand Toggle */}
              {data?.recentActivity?.length > 0 && <div className="mt-6 relative z-20">
              <button 
                onClick={() => setShowAllActivity(!showAllActivity)}
                className="w-full py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 hover:text-white transition-all"
              >
                {showAllActivity ? "Collapse Journal" : "View Full History"}
              </button>
            </div>}
            </div>

            {/* Quick Support Card */}
            <div className="p-6 bg-gradient-to-br from-orange-500/10 to-red-600/10 border border-orange-500/10 rounded-[2rem]">
              <p className="text-xs font-black text-orange-500 uppercase tracking-widest mb-2">Need Help?</p>
              <h4 className="text-sm font-bold text-white mb-4">Read our documentation for CLI integration.</h4>
              <Link to="/dashboard/docs" className="inline-flex items-center text-[10px] font-black uppercase tracking-widest text-white hover:gap-2 transition-all">
                Documentation <ExternalLink size={12} className="ml-2" />
              </Link>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}